import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/core/hooks';
import {
  Users,
  Calendar,
  ClipboardList,
  DollarSign,
  ShieldCheck,
  Settings,
  Activity,
  FileText,
  UserCog,
  CheckCircle,
  Stethoscope,
  Calculator,
  MapPin,
  Route,
  Sparkles,
} from 'lucide-react';

interface AdminCardProps {
  title: string;
  value: string;
  description: string;
  icon: React.ReactNode;
  onClick?: () => void;
}

const AdminCard: React.FC<AdminCardProps> = ({ title, value, description, icon, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="rounded-lg border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-[#6c3a80]/40 hover:shadow-md"
  >
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-sm font-medium text-slate-500">{title}</p>
        <p className="mt-2 text-3xl font-bold text-slate-950">{value}</p>
        <p className="mt-2 text-sm leading-6 text-slate-600">{description}</p>
      </div>
      <div className="rounded-md bg-[#6c3a80]/10 p-3 text-[#6c3a80]">{icon}</div>
    </div>
  </button>
);

const QuickAction: React.FC<{
  title: string;
  description: string;
  icon: React.ReactNode;
  onClick: () => void;
}> = ({ title, description, icon, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex w-full items-center gap-4 rounded-lg border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:border-[#6c3a80]/40 hover:bg-[#6c3a80]/5"
  >
    <div className="rounded-md bg-[#6c3a80]/10 p-3 text-[#6c3a80]">{icon}</div>
    <div>
      <p className="font-semibold text-slate-950">{title}</p>
      <p className="mt-1 text-sm text-slate-600">{description}</p>
    </div>
  </button>
);

export const AdministratorDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSuperAdmin = user?.roles.includes('SUPER_ADMIN') ?? false;

  const cards: AdminCardProps[] = [
    {
      title: 'Administrator',
      value: isSuperAdmin ? 'Owner Access' : 'Admin Access',
      description: isSuperAdmin
        ? 'System owner account with all application permissions enabled.'
        : 'Administrator account with operational permissions enabled.',
      icon: <ShieldCheck className="h-6 w-6" />,
      onClick: () => navigate('/settings'),
    },
    {
      title: 'Clients',
      value: 'Manage',
      description: 'Create, edit, review, and manage client records.',
      icon: <Users className="h-6 w-6" />,
      onClick: () => navigate('/clients'),
    },
    {
      title: 'Scheduling',
      value: 'Control',
      description: 'Manage visits, calendars, care delivery, and operations.',
      icon: <Calendar className="h-6 w-6" />,
      onClick: () => navigate('/scheduling'),
    },
    {
      title: 'Care Plans',
      value: 'Author',
      description: 'Build and update care plans, tasks, and service workflows.',
      icon: <ClipboardList className="h-6 w-6" />,
      onClick: () => navigate('/care-plans'),
    },
    {
      title: 'Billing',
      value: 'Enabled',
      description: 'Access invoicing, payroll, and financial administration.',
      icon: <DollarSign className="h-6 w-6" />,
      onClick: () => navigate('/billing'),
    },
    {
      title: 'Compliance',
      value: 'Enabled',
      description: 'Review quality assurance, incidents, EVV, and compliance areas.',
      icon: <CheckCircle className="h-6 w-6" />,
      onClick: () => navigate('/compliance'),
    },
  ];

  return (
    <div className="space-y-8">
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-5">
            <img
              src="/images/fuchsia-logo-mark.png"
              alt="FUCHSIA HOME CARE CATALIN"
              className="h-20 w-20 rounded-lg object-contain"
            />
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-[#6c3a80]">
                {isSuperAdmin ? 'Owner Dashboard' : 'Admin Dashboard'}
              </p>
              <h1 className="mt-1 text-3xl font-bold text-slate-950">
                FUCHSIA HOME CARE CATALIN
              </h1>
              <p className="mt-2 text-sm text-slate-600">
                Signed in as {user?.email ?? 'administrator account'} with administrator permissions.
              </p>
            </div>
          </div>
          <div className="rounded-lg bg-[#6c3a80]/10 px-4 py-3 text-sm font-medium text-[#5e3474]">
            permissions: {isSuperAdmin ? 'owner' : 'admin'}
          </div>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {cards.map((card) => (
          <AdminCard key={card.title} {...card} />
        ))}
      </section>

      <section className="grid gap-6 xl:grid-cols-[1fr_0.8fr]">
        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-xl font-bold text-slate-950">Administrator Actions</h2>
          <div className="mt-5 grid gap-3 md:grid-cols-2">
            <QuickAction
              title="Manage Settings"
              description="Company profile, branding, and system settings."
              icon={<Settings className="h-5 w-5" />}
              onClick={() => navigate('/settings')}
            />
            <QuickAction
              title="User Administration"
              description="Review users and permission-related areas."
              icon={<UserCog className="h-5 w-5" />}
              onClick={() => navigate('/admin')}
            />
            <QuickAction
              title="Reports"
              description="Open reporting and analytics sections."
              icon={<FileText className="h-5 w-5" />}
              onClick={() => navigate('/analytics/reports')}
            />
            <QuickAction
              title="Carer Payroll"
              description="Set hourly rates, calculate salaries, and generate payroll PDFs."
              icon={<Calculator className="h-5 w-5" />}
              onClick={() => navigate('/carer-payroll')}
            />
            <QuickAction
              title="Admin AI Assistant"
              description="Ask operational questions, run natural commands, generate reports, and review urgent alerts."
              icon={<Sparkles className="h-5 w-5" />}
              onClick={() => navigate('/admin-ai')}
            />
            <QuickAction
              title="Schedule Optimizer"
              description="Reduce travel, overtime and unnecessary rota cost before confirming visits."
              icon={<Route className="h-5 w-5" />}
              onClick={() => navigate('/schedule-optimizer')}
            />
            <QuickAction
              title="Live Carer Tracking"
              description="View each carer's live location, current client and active task."
              icon={<MapPin className="h-5 w-5" />}
              onClick={() => navigate('/live-carers')}
            />
            {isSuperAdmin && (
              <QuickAction
                title="Implementation Readiness"
                description="Track Carer App, Family App, GP Connect, eMAR, CQC, workflows, reporting, and security."
                icon={<ShieldCheck className="h-5 w-5" />}
                onClick={() => navigate('/enterprise-readiness')}
              />
            )}
            {isSuperAdmin && (
              <QuickAction
                title="GP Connect"
                description="Prepare NHS onboarding, Spine connectivity, assurance and audit controls."
                icon={<Stethoscope className="h-5 w-5" />}
                onClick={() => navigate('/gp-connect')}
              />
            )}
            <QuickAction
              title="Operations"
              description="Open the coordinator operations dashboard."
              icon={<Activity className="h-5 w-5" />}
              onClick={() => navigate('/analytics/coordinator')}
            />
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-xl font-bold text-slate-950">Company Contact</h2>
          <div className="mt-5 space-y-3 text-sm leading-6 text-slate-700">
            <p>Yarmouth Business Park, Suffolk Rd, Great Yarmouth, NR31 0LN</p>
            <p>Tel: 01493738488</p>
            <p>On Call: 07443145811</p>
            <p>Email: lowestoft@fuchsiahomecare.co.uk</p>
          </div>
        </div>
      </section>
    </div>
  );
};

